import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, Briefcase, Star, StarOff, TrendingUp, TrendingDown, X } from "lucide-react";
import { searchStocks, addToWatchlist, removeFromWatchlist } from "../lib/api";

export default function GlobalSearch({ onStockClick }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.length < 1) { setResults(null); return; }
    setLoading(true);
    try {
      const { data } = await searchStocks(q);
      setResults(data);
    } catch { setResults(null); }
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.length >= 1) {
      debounceRef.current = setTimeout(() => doSearch(query), 250);
    } else {
      setResults(null);
    }
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Cmd+K or Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = (symbol) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    onStockClick?.(symbol);
  };

  const handleWatchlist = async (e, symbol, isWatched) => {
    e.stopPropagation();
    try {
      if (isWatched) {
        await removeFromWatchlist(symbol);
      } else {
        await addToWatchlist(symbol);
      }
      doSearch(query); // Refresh results
    } catch {}
  };

  const hasResults = results && (results.portfolio?.length > 0 || results.market?.length > 0 || results.watchlist?.length > 0);

  return (
    <div ref={containerRef} className="relative min-w-0 w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search stocks..."
          className="w-full bg-[var(--bg-base)] border border-white/10 rounded-lg pl-9 pr-8 py-2 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-cyan-500/40 transition-colors"
          data-testid="search-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults(null); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-white">
            <X size={14} />
          </button>
        )}
      </div>

      {open && (query.length >= 1 || hasResults) && (
        <div className="fixed left-3 right-3 top-[3.75rem] sm:absolute sm:top-full sm:left-0 sm:right-0 sm:mt-1 bg-[var(--bg-surface)] border border-white/10 rounded-lg shadow-2xl z-50 max-h-[min(70vh,400px)] overflow-y-auto" data-testid="search-results">
          {loading && <div className="px-4 py-3 text-xs text-[var(--text-muted)]">Searching...</div>}

          {!loading && !hasResults && query.length >= 2 && (
            <div className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">No stocks found for "{query}"</div>
          )}

          {/* Portfolio matches */}
          {results?.portfolio?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-[var(--text-muted)] bg-white/[0.02] font-semibold">In Your Portfolio</div>
              {results.portfolio.map(s => (
                <button key={s.symbol} onClick={() => handleSelect(s.symbol)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.04] transition-colors text-left" data-testid={`result-${s.symbol}`}>
                  <div className="flex items-center gap-2.5">
                    <Briefcase size={13} className="text-cyan-400 flex-shrink-0" />
                    <div>
                      <span className="font-mono text-sm text-white">{s.symbol}</span>
                      <span className="text-[10px] text-[var(--text-muted)] ml-2">{s.sector}</span>
                      <div className="text-[10px] text-[var(--text-muted)]">{s.account_name}</div>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <div>
                      <div className="font-mono text-xs text-white">{s.last_price?.toLocaleString()}</div>
                      <div className={`font-mono text-[10px] ${s.pnl_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                        {s.pnl_pct >= 0 ? "+" : ""}{s.pnl_pct?.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Watchlist matches */}
          {results?.watchlist?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-[var(--text-muted)] bg-white/[0.02] font-semibold">Watchlist</div>
              {results.watchlist.map(s => (
                <button key={s.symbol} onClick={() => handleSelect(s.symbol)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.04] transition-colors text-left">
                  <div className="flex items-center gap-2.5">
                    <Star size={13} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                    <div>
                      <span className="font-mono text-sm text-white">{s.symbol}</span>
                      <span className="text-[10px] text-[var(--text-muted)] ml-2">{s.name}</span>
                    </div>
                  </div>
                  <button onClick={(e) => handleWatchlist(e, s.symbol, true)} className="text-amber-400 hover:text-amber-300 p-1">
                    <StarOff size={14} />
                  </button>
                </button>
              ))}
            </div>
          )}

          {/* Market matches */}
          {results?.market?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-[var(--text-muted)] bg-white/[0.02] font-semibold">NSE Stocks</div>
              {results.market.map(s => (
                <button key={s.symbol} onClick={() => handleSelect(s.symbol)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.04] transition-colors text-left" data-testid={`market-${s.symbol}`}>
                  <div className="flex items-center gap-2.5">
                    <TrendingUp size={13} className="text-[var(--text-muted)] flex-shrink-0" />
                    <div>
                      <span className="font-mono text-sm text-white">{s.symbol}</span>
                      {s.name && <span className="text-[10px] text-[var(--text-muted)] ml-2">{s.name}</span>}
                      {s.sector && <div className="text-[10px] text-[var(--text-muted)]">{s.sector}</div>}
                    </div>
                  </div>
                  <button onClick={(e) => handleWatchlist(e, s.symbol, false)} className="text-[var(--text-muted)] hover:text-amber-400 p-1" title="Add to watchlist">
                    <Star size={14} />
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
